import type { CapabilityContext } from '@neogeniux/api-contracts';
import { useMemo } from 'react';
import { useMySubscriptionItems } from '@app/shared/hooks/subscription.hook';
import { useSellableCatalog } from '@app/shared/hooks/sellable-catalog.hook';

/**
 * FE mirror of the backend `CapabilityService`. Resolves
 * `hasFeature(code)` by walking the business's owned
 * subscription items (modules + addons) and consulting the
 * sellable catalog for each item's `unlocks` array.
 *
 * A request like `hasFeature('promotions.enabled')` returns true
 * when ANY owned + included item unlocks the matching feature.
 * Catalog items' `unlocks` field is the source of truth; the FE
 * doesn't need to know which module / addon enables what.
 */
export interface CapabilityHook {
  hasFeature: (featureCode: string, ctx?: CapabilityContext) => boolean;
  hasEntitlement: (entitlementKey: string, ctx?: CapabilityContext) => boolean;
  getLimit: (limitCode: string, ctx?: CapabilityContext) => number | null;
  /** True until the underlying queries settle. Consumers gate
   *  paywalls on this to avoid flashing a "no access" screen. */
  isLoading: boolean;
}

export const useCapability = (): CapabilityHook => {
  const { data: ownedItems = [], isLoading: itemsLoading } =
    useMySubscriptionItems();
  const { data: catalog, isLoading: catalogLoading } = useSellableCatalog();

  const enabledFeatures = useMemo(() => {
    const set = new Set<string>();
    if (!catalog) return set;
    // Core product is always-on (bundled in the platform). Add its
    // unlocks unconditionally so basic features remain available
    // even if the operator hasn't purchased any extra module.
    if (catalog.core?.unlocks) {
      for (const u of catalog.core.unlocks) set.add(u);
    }
    // Mirror backend CapabilityService (capability.service.ts:213-215):
    // DO NOT gate feature codes on `subscriptionStatus`. Interceptor
    // owns lifecycle blocking (past_due / suspended → 402 on writes);
    // capability owns entitlements. A past_due tenant that owns
    // `addon.ecf` still has the `ecf.enabled` capability — otherwise
    // pages like /configuration/ecf-certificates render the paywall
    // even though the plan grants it, blocking cert-onboarding recovery.
    const ownedCodes = new Set(
      ownedItems
        .filter((i) => i.isIncluded)
        .map((i) => i.itemCode.toLowerCase())
    );
    for (const item of catalog.items ?? []) {
      if (!ownedCodes.has(item.code.toLowerCase())) continue;
      for (const u of item.unlocks ?? []) set.add(u);
    }
    return set;
  }, [ownedItems, catalog]);

  return {
    hasFeature: (featureCode: string) => enabledFeatures.has(featureCode),
    hasEntitlement: () => true,
    getLimit: () => null,
    isLoading: itemsLoading || catalogLoading,
  };
};
